import React from 'react';
import {Helmet} from 'react-helmet-async';
// @mui
import {alpha, Box, Button, Card, CardContent, Container, Grid, Link, Stack, Typography} from '@mui/material';
import {Assignment, AutoAwesome, CloudUpload, Dashboard, DiamondOutlined, Done, FactCheck, Link as LinkIcon, MenuBook, Science, Security, Storage, VisibilityOff} from '@mui/icons-material';

// routes
import {Link as RouterLink, useLocation} from 'react-router-dom';

// scroll
import {Element} from 'react-scroll';

// ----------------------------------------------------------------------

const PlaceholderImage = ({title, height = 400}) => (<Box
    sx={{
        width: '100%',
        height: height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        borderRadius: 3,
        border: '2px dashed',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
        color: 'text.secondary',
        textAlign: 'center',
        p: 4
    }}
>
    <Stack spacing={2} alignItems="center">
        <VisibilityOff sx={{fontSize: 48, opacity: 0.3}}/>
        <Typography variant="subtitle1" sx={{fontWeight: 600, opacity: 0.5}}>
            {title} <br/>
            [Platform Screenshot Placeholder]
        </Typography>
    </Stack>
</Box>);

const HeroSection = ({variant = 'default'}) => {
    if (variant === 'visual') {
        return (<Box sx={{py: {xs: 6, md: 8}}}>
            <Grid container spacing={6} alignItems="center">
                <Grid item xs={12} md={6}>
                    <Typography variant="h1" sx={{mb: 3, fontWeight: 800}}>
                        Build, validate and manage food formulations in one platform
                    </Typography>
                    <Typography variant="h5" sx={{mb: 5, fontWeight: 400, lineHeight: 1.6, color: 'text.primary', opacity: 0.72}}>
                        CibusAI connects formulation, ingredient, regulatory and development data so food R&D teams can build products with more clarity, traceability and control.
                    </Typography>
                    <Stack
    direction={{xs: 'column', sm: 'row'}}
    spacing={2}
    sx={{mb: 3.5}}
>
                        <Button
                            variant="outlined"
                            size="large"
                            component="a"
                            href="mailto:info@omniumai.com"
                            sx={{px: 4, py: 1.5, fontSize: '1.1rem'}}
                        >
                            Book a demo
                        </Button>
                        <Button
                            variant="contained"
                            component={RouterLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            to="https://app.cibus.omniumai.com/">
                            Sign Up
                        </Button>
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={2}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{
    color: 'text.primary',
    opacity: 0.72
}}
                    >
                        {[{icon: <LinkIcon sx={{fontSize: 18}}/>, label: 'Connected formulation data'}, {icon: <AutoAwesome sx={{fontSize: 18}}/>, label: 'AI-assisted workflows'}, {
                            icon: <Science sx={{fontSize: 18}}/>, label: 'Built for food R&D'
                        }].map((chip, i) => (<Stack key={i} direction="row" spacing={1} alignItems="center">
                            <Box sx={{color: 'primary.main', display: 'flex'}}>{chip.icon}</Box>
                            <Typography variant="caption" sx={{fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5}}>
                                {chip.label}
                            </Typography>
                        </Stack>))}
                    </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box
                        component="img"
                        src="/assets/CibusAI.svg"
                        alt="CibusAI Platform Overview"
                        sx={{
                            width: '100%', height: 'auto', maxHeight: 520, objectFit: 'contain', display: 'block', filter: 'drop-shadow(0 24px 40px rgba(15, 23, 42, 0.12))'
                        }}
                    />
                </Grid>
            </Grid>
        </Box>);
    }

    return (<Box sx={{py: {xs: 6, md: 8}}}>
        <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
                <Typography variant="h1" sx={{mb: 3, fontWeight: 800}}>
                    Build, validate and manage food formulations in one platform
                </Typography>
                <Typography variant="h4" color="text.secondary" sx={{mb: 5, fontWeight: 400, lineHeight: 1.5}}>
                    CibusAI helps food companies centralise formulation data, accelerate product development and support regulatory decision-making across the full formulation lifecycle.
                </Typography>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                    <Button
                        variant="contained"
                        size="large"
                        component="a"
                        href="mailto:info@omniumai.com"
                        sx={{px: 4, py: 1.5, fontSize: '1.1rem'}}
                    >
                        Book a demo
                    </Button>
                </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
                <PlaceholderImage title="CibusAI Platform Overview" height={480}/>
            </Grid>
        </Grid>
    </Box>);
};


const CompactProblemSection = () => {
    const problems = [{
        icon: <Storage sx={{fontSize: 24}}/>, title: 'Scattered formulation data', description: 'Recipes, ingredient specs and documents are often spread across files, tools and teams.',
    }, {
        icon: <FactCheck sx={{fontSize: 24}}/>, title: 'Manual validation work', description: 'Nutrition, labelling and regulatory checks take time and are hard to keep consistent.',
    }, {
        icon: <VisibilityOff sx={{fontSize: 24}}/>, title: 'Limited development visibility', description: 'Teams struggle to track formulation versions, review status and stage progression.',
    },];

    return (<Box sx={{py: 8, borderTop: '1px solid', borderColor: 'divider'}}>
        <Box sx={{mb: 6, textAlign: 'center'}}>
            <Typography variant="h3" sx={{mb: 2, fontWeight: 700}}>
                Food product development is still too fragmented
            </Typography>
        </Box>
        <Grid container spacing={3} justifyContent="center">
            {problems.map((problem, index) => (<Grid item xs={12} sm={4} key={index}>
                <Card sx={{
                    height: '100%', bgcolor: (theme) => alpha(theme.palette.error.main, 0.02), border: '1px solid', borderColor: (theme) => alpha(theme.palette.error.main, 0.08), boxShadow: 'none',
                }}>
                    <CardContent sx={{p: 3, textAlign: 'center'}}>
                        <Box sx={{mb: 1.5, color: 'error.main', display: 'flex', justifyContent: 'center'}}>{problem.icon}</Box>
                        <Typography variant="subtitle1" sx={{mb: 0.5, fontWeight: 800}}>{problem.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{problem.description}</Typography>
                    </CardContent>
                </Card>
            </Grid>))}
        </Grid>
    </Box>);
};
const visualModules = [{
    id: 'flm',
    slug: 'formulation-lifecycle-manager',
    title: 'Formulation Lifecycle Manager',
    icon: <Assignment/>,
    desc: 'Lifecycle management.',
    tags: ['Versions', 'Stages', 'Reviews'],
    spotlightOverline: 'Formulation Lifecycle Management',
    spotlightTitle: 'Manage products from idea to production',
    spotlightDescription: 'Track products, projects and formulation versions across development stages, reviews and reformulation history.',
    result: 'Clearer development pipelines, better traceability and stronger control over formulation decisions.',
    capabilities: ['Track development stages', 'Manage formulation versions', 'Review and approve product changes'],
    screenshotTitle: 'FLM Dashboard',
    image: '/assets/formulations_flm_new.png'
}, {
    id: 'formulation-designer',
    slug: 'formulation-designer',
    title: 'Formulation Designer',
    icon: <Science/>,
    desc: 'Real-time design tools.',
    tags: ['Nutri-calc', 'Comparison', 'Costing'],
    spotlightOverline: 'Formulation Designer',
    spotlightTitle: 'From formulation idea to validated product data',
    spotlightDescription: 'Build, calculate and compare formulations in one connected workspace that links directly to your ingredient library and regulatory requirements.',
    result: 'Faster formulation cycles and better product decisions before moving to production.',
    capabilities: ['Build and adjust formulations', 'Compare versions side by side', 'Connect outputs to lifecycle documentation'],
    screenshotTitle: 'Formulation Designer Workspace',
    image: "/assets/formulation_designer_v1.png"
}, {
    id: 'ingredient-library',
    slug: 'ingredient-library',
    title: 'Ingredient Library',
    icon: <MenuBook/>,
    desc: 'Searchable technical data.',
    tags: ['Search', 'Identifiers', 'Nutrition'],
    spotlightOverline: 'Ingredient Library',
    spotlightTitle: 'Searchable ingredient data in one place',
    spotlightDescription: 'Explore and organise ingredient data from internal records, supplier documents and external datasets.',
    result: 'Faster ingredient discovery and more consistent ingredient data across formulation workflows.',
    capabilities: ['Search by name, description or identifiers', 'Filter by origin, type and availability', 'Connect ingredient data to formulations'],
    screenshotTitle: 'Ingredient Library Workspace',
    image: '/assets/ingredient_library_v5.png'
}, {
    id: 'development-assistant',
    slug: 'development-assistant',
    title: 'Development Assistant',
    icon: <AutoAwesome/>,
    desc: 'Regulatory AI assistance.',
    tags: ['Regulatory Q&A', 'Labels', 'Sources'],
    spotlightOverline: 'Development Assistant',
    spotlightTitle: 'Ask questions across regulations and platform data',
    spotlightDescription: 'Use context-aware assistance to navigate regulatory guidance, formulation data and ingredient information.',
    result: 'Less time searching through fragmented information and more confidence during development.',
    capabilities: ['Ask regulatory and formulation questions', 'Support EU and U.S. food regulation context', 'Use ingredient and formulation context in answers'],
    screenshotTitle: 'Development Assistant Interface',
    image: '/assets/assistant_gpt_new.png'
}, {
    id: 'management-panel',
    slug: 'management-panel',
    title: 'Management Panel',
    icon: <Dashboard/>,
    desc: 'Activity and oversight.',
    tags: ['Dashboard', 'Activity', 'Configuration'],
    spotlightOverline: 'Management Panel',
    spotlightTitle: 'Monitor platform activity and development progress',
    spotlightDescription: 'Give administrators visibility over products, formulations, ingredients, imports and platform activity from a central dashboard.',
    result: 'Better operational oversight and clearer visibility over development activity.',
    capabilities: ['Monitor active products and projects', 'Track ingredient and formulation activity', 'Review imports, alerts and platform events'],
    screenshotTitle: 'Management Panel Overview',
    image: '/assets/management_area.png'
}, {
    id: 'data-integration',
    slug: 'data-integration',
    title: 'Data Integration',
    icon: <CloudUpload/>,
    desc: 'Connect internal data.',
    tags: ['Onboarding', 'API', 'Sync'],
    spotlightOverline: 'Data Integration',
    spotlightTitle: 'Connect formulation data with existing workflows',
    spotlightDescription: 'Support onboarding and synchronisation of formulation data through bulk imports, supplier technical data sheets and enterprise integrations.',
    result: 'Faster data onboarding and smoother connection between R&D, documentation and production workflows.',
    capabilities: ['Bulk upload tabular formulation data', 'Ingest supplier technical data sheets', 'Support legacy migration and enterprise integrations'],
    screenshotTitle: 'Data Integration Hub',
    image: '/assets/import_ingredient.png'
}];

const VisualModuleExplorerSection = ({selectedModuleId, onSelectModule}) => {
    const activeModule = visualModules.find((m) => m.id === selectedModuleId);

    return (<Box
        id="features"
        sx={{
            py: {xs: 10, md: 15}, borderTop: '1px solid', borderColor: 'divider', scrollMarginTop: {xs: 80, md: 100}
        }}
    >
        <Box sx={{textAlign: 'center', mb: 8}}>
            <Typography variant="h2" sx={{mb: 3}}>
                A connected workspace for food R&D
            </Typography>
        </Box>

        <Grid container spacing={4}>
            {/* Left Column: Module Selection */}
            <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                    {visualModules.map((m) => {
                        const isSelected = selectedModuleId === m.id;
                        return (<Card
                            key={m.id}
                            onClick={() => onSelectModule(m.id)}
                            sx={{
                                cursor: 'pointer',
                                border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                boxShadow: isSelected ? (theme) => theme.customShadows?.z8 : 'none',
                                bgcolor: isSelected ? (theme) => alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                                transition: '0.3s',
                                '&:hover': {
                                    borderColor: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.01)
                                }
                            }}
                        >
                            <CardContent sx={{p: 2.5}}>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Box sx={{color: isSelected ? 'primary.main' : 'text.secondary', display: 'flex'}}>
                                        {React.cloneElement(m.icon, {sx: {fontSize: 24}})}
                                    </Box>
                                    <Box sx={{flexGrow: 1}}>
                                        <Typography variant="subtitle1" sx={{fontWeight: 800, color: isSelected ? 'primary.main' : 'text.primary', lineHeight: 1.2}}>
                                            {m.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{display: {xs: 'none', lg: 'block'}}}>
                                            {m.desc}
                                        </Typography>
                                    </Box>
                                    {isSelected && <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main'}}/>}
                                </Stack>
                            </CardContent>
                        </Card>);
                    })}
                </Stack>
            </Grid>

            {/* Right Column: Dynamic Spotlight Content */}
            <Grid item xs={12} md={8}>
                <Box
                    sx={{
                        position: {md: 'sticky'},
                        top: {md: 96},
                        p: {xs: 3, md: 3.5},
                        borderRadius: 3,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.025),
                        border: '1px solid',
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        minHeight: {md: 500}
                    }}
                >
                    {activeModule && (<Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} lg={4.5}>
                            <Typography
                                variant="overline"
                                color="primary"
                                sx={{fontWeight: 800, mb: 1, display: 'block'}}
                            >
                                {activeModule.spotlightOverline}
                            </Typography>

                            <Typography
                                variant="h3"
                                sx={{
                                    mb: 2, fontSize: {xs: '1.75rem', md: '2rem'}, lineHeight: 1.15
                                }}
                            >
                                {activeModule.spotlightTitle}
                            </Typography>

                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{mb: 3, lineHeight: 1.6}}
                            >
                                {activeModule.spotlightDescription}
                            </Typography>

                            <Stack spacing={1.5} sx={{mb: 3}}>
                                {activeModule.capabilities.map((f, i) => (<Stack key={i} direction="row" spacing={1.5} alignItems="center">
                                    <Done sx={{color: 'primary.main', fontSize: 18}}/>
                                    <Typography variant="body2" fontWeight="600">
                                        {f}
                                    </Typography>
                                </Stack>))}
                            </Stack>

                            <Box
                                sx={{
                                    p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.035), borderLeft: '4px solid', borderLeftColor: 'primary.main'
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 800, textTransform: 'uppercase', color: 'text.primary', letterSpacing: 0.5, display: 'block', mb: 0.5
                                    }}
                                >
                                    Why it matters
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.5}}>
                                    {activeModule.result}
                                </Typography>
                            </Box>

                            <Button
                                component={RouterLink}
                                to={`/features/${activeModule.slug}`}
                                variant="outlined"
                                sx={{mt: 3}}
                            >
                                Learn more
                            </Button>
                        </Grid>

                        <Grid item xs={12} lg={7.5}>
                            <Box
                                sx={{
                                    width: '100%', borderRadius: 3, overflow: 'hidden', bgcolor: 'background.paper', boxShadow: '0 24px 45px rgba(15, 23, 42, 0.14)', border: '1px solid', borderColor: 'divider'
                                }}
                            >
                                <Box
                                    component="img"
                                    src={activeModule.image}
                                    alt={activeModule.screenshotTitle}
                                    sx={{
                                        width: '100%', height: 'auto', display: 'block'
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>)}
                </Box>
            </Grid>
        </Grid>
    </Box>);
};

function PrivacyByDesign() {
    const items = [
        {
            icon: <VisibilityOff sx={{fontSize: 22}}/>,
            title: 'Private by default',
            description: 'Sensitive formulation, ingredient and product data stays within the configured workspace.',
        },
        {
            icon: <Security sx={{fontSize: 22}}/>,
            title: 'Controlled access',
            description: 'Teams can manage who can view, edit and use product development information.',
        },
        {
            icon: <DiamondOutlined sx={{fontSize: 22}}/>,
            title: 'Your data remains yours',
            description: 'CibusAI is designed around data ownership, traceability and confidentiality.',
        },
    ];

    return (
        <Element id="privacy" name="privacy">
            <Box
                sx={{
                    py: {xs: 8, md: 12},
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Grid container spacing={5} alignItems="center">
                    <Grid item xs={12} md={4.5}>
                        <Typography
                            variant="overline"
                            color="primary"
                            sx={{
                                mb: 2,
                                display: 'block',
                                fontWeight: 800,
                            }}
                        >
                            Security & Data Ownership
                        </Typography>

                        <Typography variant="h2" sx={{mb: 3}}>
                            Built around confidentiality
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                color: 'text.primary',
                                opacity: 0.72,
                                lineHeight: 1.7,
                                maxWidth: 520,
                            }}
                        >
                            CibusAI helps teams work with sensitive formulation and ingredient
                            data while keeping ownership, access and traceability under control.
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={7.5}>
                        <Grid container spacing={2.5}>
                            {items.map((item) => (
                                <Grid item xs={12} sm={4} key={item.title}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            boxShadow: 'none',
                                            bgcolor: 'background.paper',
                                        }}
                                    >
                                        <CardContent sx={{p: 3}}>
                                            <Box
                                                sx={{
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: 2,
                                                    mb: 2,
                                                    bgcolor: (theme) =>
                                                        alpha(theme.palette.primary.main, 0.08),
                                                    color: 'primary.main',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {item.icon}
                                            </Box>

                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    mb: 1,
                                                    fontWeight: 800,
                                                    lineHeight: 1.25,
                                                }}
                                            >
                                                {item.title}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.primary',
                                                    opacity: 0.68,
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {item.description}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        </Element>
    );
}

function PilotCallToAction({variant = 'default'}) {
    const isVisual = variant === 'visual';
    return (<Element id="contact" name="contact">
        <Box sx={{
            py: {xs: 10, md: 15},
            px: 4,
            textAlign: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            borderRadius: {xs: 0, md: 4},
            mx: {xs: -3, md: 0},
            mb: {xs: 12, md: 20}
        }}>
            <Typography variant="h2" sx={{mb: 3}}>
                {isVisual ? "Ready to see CibusAI in action?" : "Accelerate your product development"}
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{mb: 5, maxWidth: 700, mx: 'auto', fontWeight: 400}}>
                {isVisual ? "Book a short walkthrough to explore how CibusAI can support your formulation, ingredient and product development workflows." : "Book a short walkthrough to explore how CibusAI can support your formulation, ingredient and product development workflows."}
            </Typography>
            <Button
                variant="contained"
                size="large"
                component="a"
                href="mailto:info@omniumai.com"
                sx={{px: 6, py: 2, fontSize: '1.2rem', boxShadow: (theme) => theme.customShadows?.primary || 0}}
            >
                Book a demo
            </Button>
        </Box>
    </Element>);
}

function ProductByOmniumAI() {
    return (<Box sx={{pt: 10, pb: 4, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider'}}>
        <Stack direction="column" spacing={3} justifyContent="center" alignItems="center">
            <Typography variant="subtitle1" color="text.secondary" sx={{fontWeight: 600, opacity: 0.6}}>
                A product by
            </Typography>
            <Link
                component="a"
                href="https://www.omniumai.com"
                target="_blank"
                rel="noopener noreferrer"
            >
                <Box
                    component="img"
                    src="/assets/logo_extended.svg"
                    alt="OmniumAI"
                    sx={{
                        height: 50, filter: 'grayscale(1)', opacity: 0.7, '&:hover': {opacity: 1, filter: 'none'}, transition: '0.3s',
                    }}
                />
            </Link>
        </Stack>
    </Box>);
}

const DEFAULT_VISUAL_MODULE = visualModules[0].id;

const getModuleFromSearch = (search) => {
    const params = new URLSearchParams(search);
    const feature = params.get('feature');

    const exists = visualModules.some((m) => m.id === feature);

    return exists ? feature : DEFAULT_VISUAL_MODULE;
};

export default function HomePage() {
    const location = useLocation();

    const [selectedVisualModule, setSelectedVisualModule] = React.useState(() => getModuleFromSearch(location.search));

    React.useEffect(() => {
        const moduleFromUrl = getModuleFromSearch(location.search);
        setSelectedVisualModule(moduleFromUrl);

        if (location.hash === '#features') {
            requestAnimationFrame(() => {
                document.getElementById('features')?.scrollIntoView({
                    behavior: 'auto', block: 'start'
                });
            });
        }
    }, [location.search, location.hash]);

    return (<>
        <Helmet>
            <title> CibusAI | Food Formulation Platform </title>
        </Helmet>
        <Container maxWidth="xl">
            <HeroSection variant="visual"/>
            <CompactProblemSection/>
            <VisualModuleExplorerSection
                selectedModuleId={selectedVisualModule}
                onSelectModule={setSelectedVisualModule}
            />
            <PrivacyByDesign variant="visual"/>
            <PilotCallToAction variant="visual"/>
            <ProductByOmniumAI/>
        </Container>
    </>);
}
